import { BreakpointObserver } from '@angular/cdk/layout';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, inject, OnInit, PLATFORM_ID, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgbModal, NgbModalRef, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { Country } from 'src/app/models/market-collection/market-collection.model';
import { AuthService } from 'src/app/shared/services/auth.service';
import { CountryService } from 'src/app/shared/services/country.service';
import { SubscriptionService } from 'src/app/shared/services/subscription.service';
import { UserApiService } from 'src/app/shared/services/user-api.service';
import { input } from '@angular/core';

@Component({
   selector: 'app-header-countries-dropdown',
   templateUrl: './header-countries-dropdown.component.html',
   styleUrls: ['./header-countries-dropdown.component.scss'],
   standalone: true,
   imports: [CommonModule, NgbModule],
   changeDetection: ChangeDetectionStrategy.OnPush
})
export class HeaderCountriesDropdownComponent implements OnInit {
   private readonly observer = inject(BreakpointObserver);
   private readonly userApiService = inject(UserApiService);
   private readonly countryService = inject(CountryService);
   private readonly modalService = inject(NgbModal);
   private readonly authService = inject(AuthService);
   private readonly platformId = inject(PLATFORM_ID);
   private readonly destroyRef = inject(DestroyRef);

   readonly isDashboardDropdown = input(false);
   readonly countriesModalcontent = viewChild<ElementRef>('countriesModalcontent');

   readonly userRole = signal<string | null>(null);
   readonly isMobile = signal(false);
   readonly countriesLoader = signal(false);
   readonly isCountriesMenuOpen = signal(false);
   readonly selectedCountryCode = signal<string>('US');
   readonly selectedCountryName = signal<string>('United States');
   readonly allCountries = signal<Country[]>([]);
   readonly coveredCountries = signal<Country[]>([]);

   private countriesModalRef!: NgbModalRef;

   // Default country data
   private readonly defaultCountry: Country = {
      country: 'United States',
      countryCode: 'US',
      detailedProfileAvailable: true,
      etfEnabled: true,
      etfExchanges: ['NYSE ARCA', 'NASDAQ'],
      etfsCovered: true,
      id: 'US',
      sortOrder: 1,
      stockEnabled: true,
      stockExchanges: ['NYSE', 'NASDAQ'],
      stocksCovered: true
   };

   constructor() {
      SubscriptionService.subscriptionData
         .pipe(takeUntilDestroyed())
         .subscribe((data) => {
            this.userRole.set(data.role);
         });

      this.observer
         .observe('(max-width: 991px)')
         .pipe(takeUntilDestroyed())
         .subscribe((result) => {
            this.isMobile.set(result.matches);
         });

      this.countryService.selectedCountry$
         .pipe(takeUntilDestroyed())
         .subscribe(country => {
            this.selectedCountryCode.set(country.countryCode);
            this.selectedCountryName.set(country.country);
         });
   }

   ngOnInit(): void {
      this.getCountriesData();
   }

   getCountriesData(): void {
      if (!isPlatformBrowser(this.platformId)) return;
      this.countriesLoader.set(true);
      this.countryService.getCountries()
         .pipe(takeUntilDestroyed(this.destroyRef))
         .subscribe({
            next: (response) => {
               if (response) {
                  this.allCountries.set(response);
                  const activeCountries = response
                     .filter((country: Country) => country.stocksCovered)
                     .sort((countryA: Country, countryB: Country) => (countryA.sortOrder ?? 0) - (countryB.sortOrder ?? 0));
                  this.coveredCountries.set(activeCountries);
                  this.setInitialSelectedCountry();
               }
            },
            error: (error) => {
               console.error(error);
               this.countriesLoader.set(false);
            },
            complete: () => {
               this.countriesLoader.set(false);
            }
         });
   }

   async setInitialSelectedCountry(): Promise<void> {
      if (!isPlatformBrowser(this.platformId)) return;

      const storedCountryCode = localStorage.getItem('dataFilterCountry');
      if (storedCountryCode) return;

      try {
         const resp = await this.userApiService.fetchUserDetails();
         const foundCountry = this.allCountries().find(c => c.countryCode === resp?.default_country);
         const countryToSet = foundCountry ?? this.defaultCountry;

         this.countryService.setSelectedCountry(countryToSet);
         this.countryService.setFilterCountry(countryToSet);
      } catch (error) {
         this.countryService.setSelectedCountry(this.defaultCountry);
         this.countryService.setFilterCountry(this.defaultCountry);
      }
   }

   onCountrySelect(country: Country): void {
      this.updateUsersDefaultCountry(country);
      this.closeCountriesModal();
   }

   async updateUsersDefaultCountry(country: Country): Promise<void> {
      try {
         if (!!this.authService.getUserId()) {
            const resp = await this.userApiService.updateUsersDefaultCountry(country.countryCode);
            if (resp.status) {
               this.countryService.setSelectedCountry(country);
            }
         } else {
            this.countryService.setSelectedCountry(country);
         }
      } catch (error) {
         console.error(error);
      }
   }

   openCountriesModal() {
      const content = this.countriesModalcontent();
      if (content) {
         this.countriesModalRef = this.modalService.open(content, {
            centered: true,
            scrollable: true,
            size: 'lg'
         });
      }
   }

   closeCountriesModal(): void {
      if (this.countriesModalRef) {
         this.countriesModalRef.dismiss();
      }
   }
}